/**
 * A DESCOBERTA das Campaigns do Partner, pura (D-192; DS-684 (a) a (e)).
 *
 * A campanha acha as Campaigns numa lista pesquisável do DV360 e vincula as que escolher, por confirmação explícita. O
 * retrato do Partner vive na memória da API (o catálogo), lido no ritmo da DS-664; aqui moram as regras que não
 * dependem do Google nem do relógio do sistema, para a API e os testes lerem as mesmas:
 *
 * - a BUSCA sobre o retrato: o termo normalizado, o ID exato ou por prefixo, o nome que contém, o filtro de status, a
 *   ordem pelo nome e as páginas de 50 (o Google não filtra Campaign por nome nem a acha sem o Advertiser);
 * - a PRIORIDADE de leitura, técnica e neutra (correção 1 da autorização): primeiro os Advertisers dos vínculos não
 *   anulados desta campanha, depois os da entidade, depois os demais pelo nome. A parte comercial do pedido não entra:
 *   ela não escolhe, não privilegia nem bloqueia Advertiser. A prioridade muda só a ordem, nunca o conjunto;
 * - o ESTADO do catálogo (`lendo`, `conferindo`, `completo`, `parcial`, `falhou`), de que a tela tira a frase: a
 *   definitiva "nenhuma encontrada" só existe com `completo`;
 * - a SITUAÇÃO de cada linha para quem pede (livre, nesta campanha, em outra campanha da entidade, indisponível), sem
 *   nunca revelar a outra entidade;
 * - os relógios do ciclo: o "Atualizar lista" a cada 60 s, a espera depois de falha e da cota, e o frescor de 60 min
 *   que a confirmação exige.
 *
 * Nada aqui grava, seleciona ou vincula: a prioridade nunca escolhe Campaign, e nenhum vínculo é inferido por nome,
 * Advertiser ou empresa.
 */
import { LIMITES_DV360 } from './limites.js'

const MS_POR_SEGUNDO = 1000n
const MS_POR_MINUTO = 60n * MS_POR_SEGUNDO

function emMs(instante: string): bigint {
  const ms = Date.parse(instante)
  if (Number.isNaN(ms)) throw new RangeError(`instante inválido: ${instante}`)
  return BigInt(ms)
}

function comoInstante(ms: bigint): string {
  return new Date(Number(ms)).toISOString()
}

// ------------------------------------------------------------------------------------------------ a busca

/** O status de uma Campaign na lista: a ARCHIVED nunca está no retrato. */
export const STATUS_NA_DESCOBERTA_DV360 = ['ativa', 'pausada'] as const
export type StatusNaDescobertaDv360 = (typeof STATUS_NA_DESCOBERTA_DV360)[number]
/** O filtro de status da busca. */
export const FILTROS_DE_STATUS_DA_DESCOBERTA_DV360 = ['ativa', 'pausada', 'todas'] as const
export type FiltroDeStatusDaDescobertaDv360 = (typeof FILTROS_DE_STATUS_DA_DESCOBERTA_DV360)[number]

/** O mínimo que a busca lê de uma Campaign do retrato. */
export interface CampaignBuscavelDv360 {
  readonly campaignId: string
  readonly nome: string
  readonly status: StatusNaDescobertaDv360
}

/**
 * O termo (e o nome) como a busca os compara: decomposto (NFD) e sem os acentos, em minúsculas, com `_` e `-` virando
 * espaço e os espaços repetidos juntados. "HAVAN_S.A" e "havan s.a" são o mesmo termo; "Promoção" acha "promocao".
 */
export function normalizarTermoDaDescobertaDv360(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

/** IDs do Google como texto (passam de 2^53): o mais curto vem antes; do mesmo tamanho, a ordem dos dígitos. */
function compararIds(a: string, b: string): number {
  if (a.length !== b.length) return a.length < b.length ? -1 : 1
  if (a === b) return 0
  return a < b ? -1 : 1
}

function compararPorNome(a: { readonly nome: string; readonly id: string }, b: { readonly nome: string; readonly id: string }): number {
  const porNome = a.nome.localeCompare(b.nome, 'pt-BR', { numeric: true, sensitivity: 'base' })
  return porNome !== 0 ? porNome : compararIds(a.id, b.id)
}

export interface PedidoDaBuscaDv360 {
  /** O termo digitado: nome, parte do nome ou ID. Vazio: todas. */
  readonly q: string
  readonly status: FiltroDeStatusDaDescobertaDv360
  /** A página pedida, a partir de 1. */
  readonly pagina: number
}

export interface PaginaDaDescobertaDv360<T> {
  /** As Campaigns da página, na ordem pelo nome. */
  readonly campaigns: readonly T[]
  /** Quantas casam com o termo e o status, em todas as páginas. */
  readonly total: number
  readonly pagina: number
  readonly porPagina: number
}

/** A Campaign casa com o termo já normalizado? (o termo vazio casa com todas) */
function casaComOTermo(c: CampaignBuscavelDv360, termo: string): boolean {
  if (termo === '') return true
  if (/^\d+$/.test(termo) && c.campaignId.startsWith(termo)) return true
  return normalizarTermoDaDescobertaDv360(c.nome).includes(termo)
}

/**
 * A busca sobre o retrato (DS-684 (d)): o termo só de dígitos acha o ID exato, o ID por prefixo ou o nome que contém o
 * termo; o outro termo, o nome que contém. O filtro de status é local, a ordem é pelo nome (empate pelo ID) e as
 * páginas têm `campaignsPorPaginaDaDescoberta`. A página além da última vem vazia, com o total de sempre.
 */
export function buscarNaDescobertaDv360<T extends CampaignBuscavelDv360>(
  retrato: Iterable<T>,
  pedido: PedidoDaBuscaDv360,
): PaginaDaDescobertaDv360<T> {
  if (!Number.isInteger(pedido.pagina) || pedido.pagina < 1) {
    throw new RangeError(`página inválida: ${String(pedido.pagina)}`)
  }
  const termo = normalizarTermoDaDescobertaDv360(pedido.q)
  const casadas = Array.from(retrato).filter((c) => (pedido.status === 'todas' || c.status === pedido.status) && casaComOTermo(c, termo))
  casadas.sort((a, b) => compararPorNome({ nome: a.nome, id: a.campaignId }, { nome: b.nome, id: b.campaignId }))
  const porPagina = BigInt(LIMITES_DV360.campaignsPorPaginaDaDescoberta)
  const inicio = (BigInt(pedido.pagina) - 1n) * porPagina
  return {
    campaigns: casadas.slice(Number(inicio), Number(inicio + porPagina)),
    total: casadas.length,
    pagina: pedido.pagina,
    porPagina: LIMITES_DV360.campaignsPorPaginaDaDescoberta,
  }
}

// ------------------------------------------------------------------------------------------------ a prioridade de leitura

/** Um Advertiser do Partner, como a lista de Advertisers o trouxe. */
export interface AdvertiserNaDescobertaDv360 {
  readonly advertiserId: string
  readonly nome: string
}

/**
 * A ORDEM em que o ciclo lê as listas de Campaigns (DS-684 (c), correção 1 da autorização), técnica e neutra:
 *
 *   1. os Advertisers dos vínculos não anulados DESTA campanha;
 *   2. os dos vínculos não anulados da ENTIDADE ativa;
 *   3. os demais;
 *
 * e, dentro de cada grupo, pelo nome (empate pelo ID). A parte comercial do pedido não entra: o nome do cliente nunca
 * escolhe, privilegia nem bloqueia Advertiser. A saída é uma permutação da entrada: a prioridade muda só a ordem de
 * leitura, nunca o conjunto que aparece, e nunca seleciona nada. Sem campanha (o aquecimento), as duas listas vêm
 * vazias e a ordem é só pelo nome.
 */
export function prioridadeNaDescobertaDv360<T extends AdvertiserNaDescobertaDv360>(fatos: {
  readonly advertisers: readonly T[]
  readonly daCampanha: Iterable<string>
  readonly daEntidade: Iterable<string>
}): readonly T[] {
  const daCampanha = new Set(fatos.daCampanha)
  const daEntidade = new Set(fatos.daEntidade)
  const grupo = (a: T): number => (daCampanha.has(a.advertiserId) ? 0 : daEntidade.has(a.advertiserId) ? 1 : 2)
  return [...fatos.advertisers].sort((a, b) => {
    const ga = grupo(a)
    const gb = grupo(b)
    if (ga !== gb) return ga < gb ? -1 : 1
    return compararPorNome({ nome: a.nome, id: a.advertiserId }, { nome: b.nome, id: b.advertiserId })
  })
}

// ------------------------------------------------------------------------------------------------ o estado do catálogo

export const ESTADOS_DA_DESCOBERTA_DV360 = ['lendo', 'conferindo', 'completo', 'parcial', 'falhou'] as const
export type EstadoDaDescobertaDv360 = (typeof ESTADOS_DA_DESCOBERTA_DV360)[number]
/** A classe da falha que a resposta leva: o Google fora, a cota, a credencial, o prazo da lista ou o teto do ciclo. */
export const FALHAS_DA_DESCOBERTA_DV360 = ['indisponivel', 'cota', 'credencial', 'tempo'] as const
export type FalhaDaDescobertaDv360 = (typeof FALHAS_DA_DESCOBERTA_DV360)[number]

/** A falha que a resposta mostra quando há mais de uma: a que pede ação antes das outras. */
const PRECEDENCIA_DAS_FALHAS: readonly FalhaDaDescobertaDv360[] = ['credencial', 'cota', 'indisponivel', 'tempo']

/** A leitura de um Advertiser no retrato. */
export interface LeituraDoAdvertiserNaDescobertaDv360 {
  /** Quando a lista de Campaigns dele foi lida com sucesso pela última vez (instante ISO); `null` = nunca. */
  readonly lidoEm: string | null
  /** A falha da ÚLTIMA tentativa de ler a lista dele; `null` = a última deu certo, ou ainda não houve tentativa. */
  readonly falha: FalhaDaDescobertaDv360 | null
}

export interface EntradaDoEstadoDaDescobertaDv360 {
  readonly agora: string
  /**
   * Um por Advertiser da última lista de Advertisers que deu certo; `null` = ela nunca deu certo. O Advertiser
   * REPROVADO na leitura (o partnerId que não é o configurado, a moeda ou o fuso inválidos, o fuso que o banco não
   * conhece) CONTINUA aqui, com `lidoEm` nulo e a falha `indisponivel`, e nunca é removido: fora da conta, o estado
   * sairia `completo` sem as Campaigns dele, e a frase definitiva "nenhuma encontrada" mentiria (DS-684 (a)).
   */
  readonly advertisers: readonly LeituraDoAdvertiserNaDescobertaDv360[] | null
  /**
   * Quando a lista de Advertisers deu certo pela última vez (instante ISO); `null` exatamente quando `advertisers` é.
   * É a hora que a frase cita quando o Partner não tem Advertiser nenhum (não há lista de Campaigns para citar).
   */
  readonly listaLidaEm: string | null
  /** Há um ciclo em curso agora. */
  readonly cicloEmCurso: boolean
  /**
   * A falha do ÚLTIMO ciclo terminado que não é de um Advertiser só (a lista de Advertisers, a credencial, a cota que
   * parou o ciclo, o teto de 120 s); `null` sem ela.
   */
  readonly falhaDoCiclo: FalhaDaDescobertaDv360 | null
}

export interface EstadoDoCatalogoDv360 {
  readonly estado: EstadoDaDescobertaDv360
  /** Os Advertisers lidos dentro da validade ("k de n"). */
  readonly conferidos: number
  /** n: os Advertisers do Partner; `null` antes da primeira lista de Advertisers. */
  readonly total: number | null
  /**
   * A leitura MAIS ANTIGA entre os Advertisers lidos (a hora que a frase cita); sem Advertiser nenhum no Partner, a da
   * lista de Advertisers; `null` sem nenhuma leitura. O `completo` sempre tem a hora.
   */
  readonly lidoEm: string | null
  /** Só no `parcial` e no `falhou`. */
  readonly falha: FalhaDaDescobertaDv360 | null
}

/** A entrada lida há `minutosDeValidadeDoCatalogo` ou menos. */
function fresca(lidoEm: string | null, agora: bigint): boolean {
  return lidoEm !== null && agora - emMs(lidoEm) <= BigInt(LIMITES_DV360.minutosDeValidadeDoCatalogo) * MS_POR_MINUTO
}

/**
 * O estado do catálogo (DS-684 (a)):
 *
 * - `lendo`: algum Advertiser nunca lido, com um ciclo em curso; ou a lista de Advertisers ainda não chegou e nenhum
 *   ciclo falhou (com ciclo em curso ou ANTES do primeiro: o GET que vê isso dispara a leitura);
 * - `conferindo`: todos têm dado e algum venceu (ou um "Atualizar" relê o que estava válido), com o ciclo em curso; e
 *   também o retrato vencido SEM ciclo e sem falha, que o GET seguinte manda reler;
 * - `completo`: todos lidos há 10 min ou menos, sem falha nem ciclo em curso; o Partner sem Advertiser, com a lista
 *   de Advertisers lida há 10 min ou menos (é a hora que ele cita);
 * - `parcial`: o ciclo terminou com Advertiser em falha (inclusive o reprovado na leitura), sem ler algum, ou com falha
 *   do ciclo sobre um retrato;
 * - `falhou`: nenhum retrato, e o ciclo falhou antes de ler a lista de Advertisers.
 *
 * A falha só acompanha o `parcial` e o `falhou`; durante um ciclo, a anterior não aparece.
 */
export function estadoDaDescobertaDv360(entrada: EntradaDoEstadoDaDescobertaDv360): EstadoDoCatalogoDv360 {
  const agora = emMs(entrada.agora)
  const leituras = entrada.advertisers
  if ((leituras === null) !== (entrada.listaLidaEm === null)) {
    throw new RangeError('a lista de Advertisers e o instante dela andam juntos: os dois nulos só antes da primeira')
  }
  if (leituras === null) {
    const falhou = !entrada.cicloEmCurso && entrada.falhaDoCiclo !== null
    return { estado: falhou ? 'falhou' : 'lendo', conferidos: 0, total: null, lidoEm: null, falha: falhou ? entrada.falhaDoCiclo : null }
  }
  const conferidos = leituras.filter((l) => fresca(l.lidoEm, agora)).length
  let maisAntiga: string | null = null
  for (const l of leituras) {
    if (l.lidoEm !== null && (maisAntiga === null || emMs(l.lidoEm) < emMs(maisAntiga))) maisAntiga = l.lidoEm
  }
  // Sem Advertiser no Partner, não há lista de Campaigns: a leitura é a da própria lista de Advertisers.
  const semAdvertiser = leituras.length === 0
  const base = { conferidos, total: leituras.length, lidoEm: semAdvertiser ? entrada.listaLidaEm : maisAntiga }
  const nuncaLido = leituras.some((l) => l.lidoEm === null)
  if (entrada.cicloEmCurso) return { ...base, estado: nuncaLido ? 'lendo' : 'conferindo', falha: null }
  const falhas = [entrada.falhaDoCiclo, ...leituras.map((l) => l.falha)].filter((f) => f !== null)
  if (nuncaLido || falhas.length > 0) {
    // O Advertiser nunca lido sem falha registrada ficou de fora pelo teto do ciclo: é `tempo`.
    return { ...base, estado: 'parcial', falha: PRECEDENCIA_DAS_FALHAS.find((f) => falhas.includes(f)) ?? 'tempo' }
  }
  const vencido = semAdvertiser ? !fresca(entrada.listaLidaEm, agora) : conferidos < leituras.length
  if (vencido) return { ...base, estado: 'conferindo', falha: null }
  return { ...base, estado: 'completo', falha: null }
}

// ------------------------------------------------------------------------------------------------ a situação da linha

export const SITUACOES_NA_DESCOBERTA_DV360 = ['livre', 'nesta_campanha', 'em_outra_campanha', 'indisponivel'] as const
export type SituacaoNaDescobertaDv360 = (typeof SITUACOES_NA_DESCOBERTA_DV360)[number]

/**
 * A situação de uma Campaign da lista para QUEM PEDE (DS-684 (i)), calculada a cada pedido e nunca guardada no retrato:
 * sem vínculo vivo, `livre`; vinculada a esta campanha, `nesta_campanha`; a outra campanha da MESMA entidade,
 * `em_outra_campanha` (a tela nomeia a campanha e o status); a uma campanha de OUTRA entidade, `indisponivel`, sem
 * dizer nada dela (a Campaign alimenta uma só campanha no sistema inteiro: a unicidade é global).
 */
export function situacaoNaDescobertaDv360(fatos: {
  readonly campanhaId: string
  readonly entidadeId: string
  /** O vínculo não anulado da Campaign, se houver: a campanha e a entidade dela. */
  readonly detentor: { readonly campanhaId: string; readonly entidadeId: string } | null
}): SituacaoNaDescobertaDv360 {
  const d = fatos.detentor
  if (d === null) return 'livre'
  if (d.entidadeId !== fatos.entidadeId) return 'indisponivel'
  return d.campanhaId === fatos.campanhaId ? 'nesta_campanha' : 'em_outra_campanha'
}

// ------------------------------------------------------------------------------------------------ os relógios do ciclo

/**
 * "Atualizar lista" (DS-684 (b)): força um ciclo no máximo a cada `segundosEntreAtualizacoesForcadas` por processo, e
 * com um ciclo em curso não faz nada (ele já está lendo). `pode` é a resposta inteira. `disponivelEm` fala SÓ do
 * intervalo de 60 s: o instante em que ele deixa de segurar, ou `null` quando ele não segura — e, com um ciclo em
 * curso, `null` com `pode` falso (o pedido não faz nada). Por isso a tela desabilita "Atualizar" também nos estados
 * `lendo` e `conferindo` (há leitura em curso, ou o GET acabou de pedi-la), e não só pelo `disponivelEm`.
 */
export function atualizacaoForcadaDaDescobertaDv360(fatos: {
  readonly agora: string
  /** O instante do último ciclo forçado; `null` sem nenhum. */
  readonly ultimaForcadaEm: string | null
  readonly cicloEmCurso: boolean
}): { readonly pode: boolean; readonly disponivelEm: string | null } {
  const agora = emMs(fatos.agora)
  const liberaEm = fatos.ultimaForcadaEm === null
    ? null
    : emMs(fatos.ultimaForcadaEm) + BigInt(LIMITES_DV360.segundosEntreAtualizacoesForcadas) * MS_POR_SEGUNDO
  const disponivelEm = liberaEm !== null && liberaEm > agora ? comoInstante(liberaEm) : null
  return { pode: !fatos.cicloEmCurso && disponivelEm === null, disponivelEm }
}

/**
 * Um ciclo novo pode sair agora? (DS-684 (b)) Depois de um ciclo parcial ou falho, só `segundosDepoisDeFalha` depois do
 * fim dele; depois da cota, só depois do Retry-After (o instante que quem chama calculou do cabeçalho), o que vier
 * mais tarde.
 */
export function podeAbrirCicloDaDescobertaDv360(fatos: {
  readonly agora: string
  /** O fim do último ciclo que terminou parcial ou falho; `null` se o último terminou completo (ou não houve). */
  readonly fimDoCicloComFalha: string | null
  /** Até quando a cota pediu para esperar (Retry-After); `null` sem ela. */
  readonly cotaAte: string | null
}): boolean {
  const agora = emMs(fatos.agora)
  if (fatos.cotaAte !== null && agora < emMs(fatos.cotaAte)) return false
  if (fatos.fimDoCicloComFalha === null) return true
  return agora - emMs(fatos.fimDoCicloComFalha) >= BigInt(LIMITES_DV360.segundosDepoisDeFalha) * MS_POR_SEGUNDO
}

/**
 * A confirmação aceita a Campaign pelo catálogo? (DS-684 (e)) Só se a lista do Advertiser dela foi lida pelo servidor
 * há `minutosParaConfirmarPeloCatalogo` ou menos; senão, `lista_desatualizada` (422), e nada é gravado.
 */
export function confirmavelPeloCatalogoDv360(fatos: { readonly agora: string; readonly lidoEm: string }): boolean {
  return emMs(fatos.agora) - emMs(fatos.lidoEm) <= BigInt(LIMITES_DV360.minutosParaConfirmarPeloCatalogo) * MS_POR_MINUTO
}
